import type { ReactNode } from 'react';
import type {
  AddDocumentSource,
  AddDocumentTarget
} from '@/components/shared/add-document';

/**
 * The sources a replacement may come from, in menu order.
 *
 * Deliberately the same union as {@link AddDocumentSource} minus `precedent`: a
 * precedent is generated against the matter rather than chosen from somewhere,
 * so it answers "make me a new document", not "which copy of this document
 * should the slot hold". Widening this to include it would put a source in the
 * Replace chooser that cannot replace anything.
 */
export type ReplaceDocumentSource = Exclude<AddDocumentSource, 'precedent'>;

/** Upload → the matter's own documents → the firm's PMS. Nearest source first. */
export const DEFAULT_REPLACE_SOURCES: readonly ReplaceDocumentSource[] = [
  'upload',
  'existing',
  'pms'
];

/**
 * Narrow a surface's `AddDocument` source list to the ones that can replace.
 *
 * A surface that already builds a source list for its *empty* state hands the
 * same list here rather than restating it, and drops `precedent` by construction
 * instead of by remembering to.
 */
export function toReplaceSources(
  sources: readonly AddDocumentSource[]
): ReplaceDocumentSource[] {
  return sources.filter(
    (source): source is ReplaceDocumentSource => source !== 'precedent'
  );
}

export interface ReplaceDocumentProps {
  /** Matter the replacement lands on. Scopes the pick list, the upload and the PMS gate. */
  matterId: number;
  /**
   * The slot being replaced: the `fileType` the replacement lands as, plus the
   * per-type association ids. Identical to {@link AddDocumentTarget} on purpose:
   * a replacement is the same landing as an add, against a slot that is
   * already occupied, so the two affordances share one target contract and a
   * surface that already builds a target for its empty state reuses it verbatim.
   *
   * The target is what decides which sources apply. A surface never passes an
   * opinion about that.
   *
   * **Optional only so a surface whose target is itself conditional can still
   * call the hook unconditionally**, as the rules of hooks require. Without a
   * target there is nowhere for a replacement to land, so
   * {@link UseReplaceDocumentResult.menuItem} is `null` and the seam offers
   * nothing, which is the same "pass `undefined` and the route does not render"
   * shape the card's other resolved routes use. It is never a way to opt out of
   * declaring one.
   */
  target?: AddDocumentTarget;
  /**
   * The document being replaced. Excluded from the choose-existing picker
   * (VALE-1135): picking a document as its own replacement copies it, lands the
   * copy in the slot and archives the original, so the user ends on a different
   * file with the same bytes and a new id. There is no reading of that gesture
   * where the copy is what they wanted.
   */
  replacingFileId?: number;
  /** Sources to offer, in menu order. Defaults to {@link DEFAULT_REPLACE_SOURCES}. */
  sources?: readonly ReplaceDocumentSource[];
  /**
   * Native `<input accept>` for the Upload source's picker. Defaults to what the
   * target's own file type accepts (`acceptStringFor`, VALE-209). Pass this only
   * where the requirement is genuinely narrower than its file type.
   */
  accept?: string;
  /**
   * Explicit PMS provider display name. Usually omitted: the seam resolves it
   * from the matter's own `metadata.pms.source` via `usePmsProviderLabel`, which
   * is the single definition of "is this matter PMS-connected, and may we offer
   * its documents" (LF-6261, VALE-356). Pass it only when the caller already
   * holds the label for its own copy or gating.
   */
  pmsProviderLabel?: string;
  /**
   * The caller owns the Upload UI: selecting Upload calls this instead of
   * opening the seam's own picker, and the seam lands nothing.
   *
   * The core slots need it. Their typed dialogs carry VALE-757's extraction
   * choice ("Upload Will" versus "Upload & extract"), and filing a document is
   * not consent to read it, so a Replace that quietly uploaded through the
   * seam's own picker would drop a consent gate that no other check can see.
   */
  onSelectUpload?: () => void;
  /** Fired once a replacement has landed, from whichever source. */
  onReplaced?: () => void | Promise<void>;
  /** Fired around the seam's own upload: `true` when it starts, `false` when it settles. */
  onReplacingChange?: (isReplacing: boolean) => void;
  /** Menu-item label. Defaults to `Replace`. */
  label?: string;
  disabled?: boolean;
}

/**
 * What {@link useReplaceDocument} hands back: a menu item to put inside the
 * surface's own overflow menu, and the surfaces to render **outside** it.
 *
 * The split is forced, not stylistic. A `DropdownMenuContent` unmounts its
 * children when the menu closes, and selecting an item closes the menu, so a
 * dialog or file input rendered inside the menu is torn down in the same commit
 * that was meant to open it. That bug was found and fixed independently on
 * `letterhead-actions-menu` and `personal-signature-card`; handing the two
 * halves back separately is what stops the next surface from rediscovering it.
 */
export interface UseReplaceDocumentResult {
  /** Render inside the surface's `DropdownMenuContent`. */
  menuItem: ReactNode;
  /** Render outside the dropdown, as a sibling of it. */
  surfaces: ReactNode;
}
